/**
 * First-run / empty-documents landing for the Documents tab (embedded site mode).
 * Visual language matches SedRebuildLandingCard on Site Evidence.
 */
import React from 'react';
import { FileText, FolderOpen, Sparkles, Upload } from 'lucide-react';
import { REPORT_TYPE_REGISTRY, type ReportTypeId } from '../../config/reportTypes';
import { getDocumentChecklistAcross } from '../../lib/documentChecklist';

export interface DocumentsUploadLandingCardProps {
  recommendedReportTypes?: ReportTypeId[];
  isUploading?: boolean;
  uploadProgress?: string;
  onUploadFiles: () => void;
  onUploadFolder: () => void;
  onSkip?: () => void;
}

export const DocumentsUploadLandingCard: React.FC<DocumentsUploadLandingCardProps> = ({
  recommendedReportTypes,
  isUploading = false,
  uploadProgress,
  onUploadFiles,
  onUploadFolder,
  onSkip,
}) => {
  const siteTypeLabel = React.useMemo(() => {
    if (!recommendedReportTypes || recommendedReportTypes.length === 0) return null;
    if (recommendedReportTypes.length === 1) {
      return REPORT_TYPE_REGISTRY[recommendedReportTypes[0]!]?.shortLabel?.toLowerCase() ?? null;
    }
    return 'your planned report types';
  }, [recommendedReportTypes]);

  const checklistHints = React.useMemo(() => {
    if (!recommendedReportTypes || recommendedReportTypes.length === 0) return [];
    return getDocumentChecklistAcross(recommendedReportTypes).slice(0, 3).map(item => item.label);
  }, [recommendedReportTypes]);

  const bullets = checklistHints.length > 0
    ? checklistHints.map(label => `Recommended for ${siteTypeLabel ?? 'this site'}: ${label}`)
    : [
        'Phase I ESAs, geotech reports, plat maps, and utility studies',
        'Extracted text powers the Coverage Matrix and report variables',
        'Reports cite your files alongside public provider data',
      ];

  return (
    <div
      className="rounded-md border border-border bg-surface-1/40 p-8 max-w-2xl mx-auto text-center space-y-6"
      data-testid="documents-upload-landing"
    >
      <div className="mx-auto w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center">
        <FileText className="w-7 h-7 text-primary" aria-hidden />
      </div>
      <div className="space-y-2">
        <h3 className="text-base font-semibold text-text-primary">
          Upload site documents
        </h3>
        <p className="text-sm text-text-muted leading-relaxed max-w-md mx-auto">
          Project PDFs, studies, and drawings feed the Site Evidence Document and make
          every report more specific than public data alone. You can add files now or
          skip and generate from providers first.
        </p>
      </div>
      <ul className="text-left text-[11px] text-text-secondary space-y-2 max-w-sm mx-auto">
        {bullets.map(line => (
          <li key={line} className="flex items-start gap-2">
            <Sparkles className="w-3.5 h-3.5 text-primary shrink-0 mt-0.5" aria-hidden />
            <span>{line}</span>
          </li>
        ))}
      </ul>
      <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
        <button
          type="button"
          onClick={onUploadFiles}
          disabled={isUploading}
          className="inline-flex items-center gap-2 rounded-md px-5 py-2.5 text-[11px] font-bold uppercase tracking-widest bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          data-testid="documents-upload-landing-files"
        >
          <Upload className={`w-4 h-4 ${isUploading ? 'animate-pulse' : ''}`} aria-hidden />
          {isUploading ? (uploadProgress || 'Uploading…') : 'Upload files'}
        </button>
        <button
          type="button"
          onClick={onUploadFolder}
          disabled={isUploading}
          className="inline-flex items-center gap-2 rounded-md px-5 py-2.5 text-[11px] font-bold uppercase tracking-widest border border-border text-text-secondary hover:text-text-primary hover:border-primary disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          data-testid="documents-upload-landing-folder"
        >
          <FolderOpen className="w-4 h-4" aria-hidden />
          Upload folder
        </button>
      </div>
      {onSkip && (
        <button
          type="button"
          onClick={onSkip}
          disabled={isUploading}
          className="type-meta text-text-muted hover:text-primary underline-offset-2 hover:underline disabled:opacity-50"
          data-testid="documents-upload-landing-skip"
        >
          Skip — generate from public data
        </button>
      )}
    </div>
  );
};
